import { NgClass, KeyValuePipe } from '@angular/common';
import { Component, OnDestroy, inject } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { Subscription } from 'rxjs';
import { GroupedSearchResults, Search, SearchResultsItem } from '@bn/api-interfaces';
import { ProductTypes } from '@bn/dashboard-core/enums';
import { SearchComponent as SearchComponent_1 } from '@bn/layout/search/search.component';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { MixpanelEventsDirective } from '@bn/shared/utils/directives';
import { HighlightWordsDirective, LazyLoadDirective } from '@bn/web-ui';
import { SearchProductsConfig } from './search.config';
import { SearchService } from './search.service';

@Component({
  selector: 'bn-dashboard-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  imports: [SearchComponent_1, NgClass, FaIconComponent, HighlightWordsDirective, LazyLoadDirective, KeyValuePipe],
})
export class SearchComponent extends AbstractLazyDirective implements OnDestroy {
  private readonly searchService = inject(SearchService);
  readonly #mixpanel = inject(MixpanelEventsDirective);

  public readonly searchProductsConfig = SearchProductsConfig;
  public readonly minSearchTermLength = this.appConfig.search.minSearchTermLength;
  public readonly debounceMs = this.appConfig.search.debounceTime;

  public groupedSearchResults: GroupedSearchResults = {};
  public isOpen = false;
  public isLoading = false;
  public selectedIndex = -1;
  public searchTerm = '';

  private requestSubscription = new Subscription();

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.requestSubscription.unsubscribe();
  }

  public onSearchTermChange(term: string): void {
    this.searchTerm = term;
    this.selectedIndex = -1;
    this.runSearch(term);
  }

  public onInputClick(): void {
    this.#mixpanel.addEvent('Search Active', {});

    if (Object.keys(this.groupedSearchResults).length) {
      this.isOpen = true;
      return;
    }

    if (this.searchTerm.length >= this.minSearchTermLength) {
      this.runSearch(this.searchTerm);
    }
  }

  public onClear(): void {
    this.searchTerm = '';
    this.isLoading = false;
    this.groupedSearchResults = {};
    this.handleClose();
  }

  public onClickOutside(): void {
    this.handleClose();
  }

  public onEscape(): void {
    this.handleClose();
  }

  public onArrowUp(): void {
    this.handleArrowKeys('up');
  }

  public onArrowDown(): void {
    this.handleArrowKeys('down');
  }

  public onEnter(): void {
    if (this.selectedIndex < 0) return;

    Object.keys(this.groupedSearchResults)
      .sort()
      .forEach((groupKey) => {
        const group = this.groupedSearchResults[groupKey as ProductTypes];
        group?.searchResults?.forEach((searchResult: SearchResultsItem) => {
          if (searchResult.actualIndex === this.selectedIndex) this.onSearchItemClick(searchResult, group?.href);
        });
      });
  }

  public onSearchItemClick(item: SearchResultsItem, url: string | undefined): void {
    this.#mixpanel.addEvent('Search Result Clicked', {
      searchQuery: this.searchTerm,
      clickedItemType: item.Type ?? '',
      clickedItemId: item.Id,
    });

    this.router.navigate([`${url}/${item.Id}`]);
    this.handleClose();
  }

  private runSearch(term: string): void {
    this.isLoading = true;
    this.requestSubscription.unsubscribe();
    this.requestSubscription = this.searchService.getSearchResults(term).subscribe({
      next: (response) => this.handleSearchResponseSuccess(response),
      error: () => this.handleSearchResponseError(),
    });
  }

  private handleSearchResponseSuccess(searchResponse: Search | null): void {
    if (searchResponse) {
      this.groupResultsByType(searchResponse?.SearchResults);
      this.attachActualIndex();

      this.#mixpanel.addEvent('Search Submitted', {
        searchQuery: this.searchTerm,
        resultsFound: (searchResponse.SearchResults?.length ?? 0) > 0 ? 1 : 0,
      });

      this.isOpen = true;
    }

    this.isLoading = false;
  }

  private handleSearchResponseError(): void {
    this.isLoading = false;
  }

  private groupResultsByType(searchResults?: SearchResultsItem[]): void {
    this.groupedSearchResults = {};

    searchResults?.forEach((searchResultItem: SearchResultsItem) => {
      const type = searchResultItem.Type;
      const maxItemsPerProduct = this.appConfig.search.maxItemsPerProduct;

      if (!type) return;

      const currentItemsInPerProduct = this.groupedSearchResults[type]?.searchResults?.length || 0;

      if (maxItemsPerProduct <= currentItemsInPerProduct) return;

      if (this.groupedSearchResults[type]) {
        this.groupedSearchResults[type]?.searchResults?.push(searchResultItem);
      } else {
        this.groupedSearchResults[type] = {
          name: this.searchProductsConfig[type]?.name,
          icon: this.searchProductsConfig[type]?.icon,
          href: this.searchProductsConfig[type]?.href,
          searchResults: [searchResultItem],
        };
      }
    });
  }

  private attachActualIndex(): void {
    let actualIndex = 0;

    Object.keys(this.groupedSearchResults)
      .sort()
      .forEach((group) =>
        this.groupedSearchResults[group as ProductTypes]?.searchResults?.forEach((searchResult: SearchResultsItem) => {
          searchResult.actualIndex = actualIndex++;
        }),
      );
  }

  private handleClose(): void {
    this.isOpen = false;
    this.selectedIndex = -1;
  }

  private handleArrowKeys(direction: 'up' | 'down'): void {
    if (!this.isOpen) return;

    let totalResults = 0;

    Object.keys(this.groupedSearchResults)
      .sort()
      .forEach((group) => {
        totalResults += this.groupedSearchResults[group as ProductTypes]?.searchResults?.length ?? 0;
      });

    if (direction === 'up') {
      this.selectedIndex = this.selectedIndex === 0 ? totalResults - 1 : this.selectedIndex - 1;
    } else {
      this.selectedIndex = this.selectedIndex === totalResults - 1 ? 0 : this.selectedIndex + 1;
    }
  }
}
